import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Checkbox, Col, Divider, Input, Modal, Row, Skeleton } from 'antd';
import { TbHash, TbLock, TbPlugConnected, TbSearch } from 'react-icons/tb';

import { SlackSVGIcon } from '@/assets';
import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection, LearnMoreFooter, SmallTag } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { ACCOUNT_NOTIFICATION_SETTINGS_PATHS } from '@/shared/constants';

interface SlackChannel {
    id: string;
    isMember: boolean;
    isPrivate: boolean;
    name: string;
}

interface SlackChannelsResponse {
    channels: SlackChannel[];
    connected: boolean;
    connectedAccountId?: string;
}

interface SlackNotificationModalProps {
    connectedAccountId?: string;
    open: boolean;
    selectedChannelIds: string[];
    setOpen: (open: boolean) => void;
    onSave: (value: { channelIds: string[]; connectedAccountId: string }) => void;
}

const SlackNotificationModal: FC<SlackNotificationModalProps> = ({
    connectedAccountId,
    open,
    selectedChannelIds,
    setOpen,
    onSave,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [channels, setChannels] = useState<SlackChannel[]>([]);
    const [connectionId, setConnectionId] = useState(connectedAccountId);
    const [connected, setConnected] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [selection, setSelection] = useState<string[]>(selectedChannelIds);

    const loadChannels = async () => {
        setQuery('');
        setSelection(selectedChannelIds);
        setLoading(true);

        try {
            const { data } = await authorizedAxiosInstance.get<SlackChannelsResponse>(
                '/api/notifications/slack/channels',
            );
            setChannels(data.channels);
            setConnected(data.connected);
            setConnectionId(data.connectedAccountId);
        } catch (error) {
            captureException(error);
            setChannels([]);
            setConnected(false);
            toast.error('Slack channels could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const visibleChannels = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase();

        if (!normalizedQuery) {
            return channels;
        }

        return channels.filter((channel) => channel.name.toLocaleLowerCase().includes(normalizedQuery));
    }, [channels, query]);

    const connectSlack = async () => {
        try {
            const { data } = await authorizedAxiosInstance.get<{ url: string }>('/api/auth/account/slack/url', {
                params: { returnTo: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general },
            });

            window.location.href = data.url;
        } catch (error) {
            captureException(error);
            toast.error('Slack could not be connected.');
        }
    };

    const toggleChannel = (channelId: string, checked: boolean) => {
        setSelection((current) =>
            checked ? Array.from(new Set([...current, channelId])) : current.filter((id) => id !== channelId),
        );
    };

    return (
        <Modal
            destroyOnClose
            footer={
                <Row align='middle' gutter={[8, 0]} wrap={false}>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic='Slack notifications' />
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Button size='small' onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                    </Col>
                    <Col flex='none'>
                        <Button
                            disabled={!connected || !connectionId || selection.length === 0}
                            size='small'
                            type='primary'
                            onClick={() => {
                                if (!connectionId || selection.length === 0) {
                                    return;
                                }

                                onSave({ channelIds: selection, connectedAccountId: connectionId });
                                setOpen(false);
                            }}
                        >
                            Save Channels
                        </Button>
                    </Col>
                </Row>
            }
            onCancel={() => setOpen(false)}
            open={open}
            afterOpenChange={(isOpen) => {
                if (isOpen) {
                    void loadChannels();
                }
            }}
            title={
                <span>
                    <img
                        alt=''
                        aria-hidden
                        src={SlackSVGIcon}
                        style={{ height: '1rem', marginRight: '0.5rem', verticalAlign: '-0.15rem', width: '1rem' }}
                    />
                    Configure Slack Notifications
                </span>
            }
            width={16 * 34}
        >
            <p style={{ color: token.colorTextTertiary, marginTop: 0 }}>
                Choose the channels that can receive the Slack notifications enabled on this page.
            </p>
            {loading ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : !connected ? (
                <EmptySection
                    description='Connect a Slack workspace before choosing notification channels.'
                    height='16rem'
                    icon={<TbPlugConnected />}
                    label='Connect Slack'
                    extra={
                        <Button size='small' type='primary' onClick={connectSlack}>
                            <TbPlugConnected />
                            Connect Slack
                        </Button>
                    }
                />
            ) : (
                <>
                    <Input
                        allowClear
                        placeholder='Search Slack Channels...'
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <Divider style={{ margin: '0.75rem 0 0.5rem' }} />
                    <div
                        style={{
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            maxHeight: '20rem',
                            overflowY: 'auto',
                            padding: '0.25rem',
                        }}
                    >
                        {visibleChannels.length === 0 ? (
                            <EmptySection
                                description='Try another channel name or invite the Slack app to the channel.'
                                height='12rem'
                                icon={<TbHash />}
                                label='No Slack Channels Found'
                            />
                        ) : (
                            visibleChannels.map((channel) => (
                                <Row
                                    key={channel.id}
                                    align='middle'
                                    gutter={[8, 0]}
                                    style={{
                                        borderRadius: token.borderRadiusSM,
                                        minHeight: '2.25rem',
                                        padding: '0 0.5rem',
                                    }}
                                    wrap={false}
                                >
                                    <Col flex='none'>
                                        <Checkbox
                                            checked={selection.includes(channel.id)}
                                            disabled={!channel.isMember}
                                            onChange={(event) => toggleChannel(channel.id, event.target.checked)}
                                        />
                                    </Col>
                                    <Col flex='none' style={{ color: token.colorTextTertiary }}>
                                        {channel.isPrivate ? <TbLock /> : <TbHash />}
                                    </Col>
                                    <Col flex='auto' style={{ minWidth: 0 }}>
                                        <span style={{ color: token.colorTextSecondary }}>{channel.name}</span>
                                    </Col>
                                    {!channel.isMember && (
                                        <Col flex='none'>
                                            <SmallTag message='Join in Slack' />
                                        </Col>
                                    )}
                                </Row>
                            ))
                        )}
                    </div>
                    <small style={{ color: token.colorTextTertiary, display: 'block', marginTop: '0.5rem' }}>
                        {selection.length} {selection.length === 1 ? 'channel' : 'channels'} selected
                    </small>
                </>
            )}
        </Modal>
    );
};

export default SlackNotificationModal;
